import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Database, 
  Calendar, 
  AlertTriangle, 
  AlertCircle, 
  Users, 
  TrendingUp,
  Activity,
  ChevronDown,
  ChevronUp,
  Clock,
  FileAudio,
  SmartphoneNfc,
  Cpu,
  Usb
} from 'lucide-react';
import SettingsSectionHeader from './SettingsSectionHeader';

const Toggle = ({ checked, onChange, label, icon: Icon, description, metric, isDarkMode }) => (
  <div className="group relative">
    <button
      onClick={() => onChange(!checked)}
      className={`w-full p-5 rounded-xl border-2 transition-all duration-300 transform hover:scale-102 ${
        checked 
          ? isDarkMode
            ? 'bg-blue-900/30 border-blue-400 shadow-lg'
            : 'bg-blue-50 border-blue-500 shadow-lg'
          : isDarkMode
            ? 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
            : 'bg-white border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className={`p-3 rounded-xl transition-all duration-300 ${
            checked 
              ? isDarkMode 
                ? 'bg-blue-500/20 shadow-inner' 
                : 'bg-blue-500 shadow-inner'
              : isDarkMode 
                ? 'bg-gray-700' 
                : 'bg-gray-100'
          }`}>
            <Icon size={24} className={checked 
              ? isDarkMode 
                ? 'text-blue-300' 
                : 'text-white'
              : isDarkMode 
                ? 'text-gray-400' 
                : 'text-gray-500'
            } />
          </div>
          <div className="text-left">
            <h4 className={`font-semibold text-lg ${
              isDarkMode 
                ? checked 
                  ? 'text-blue-300' 
                  : 'text-gray-300'
                : checked 
                  ? 'text-blue-900' 
                  : 'text-gray-700'
            }`}>
              {label}
            </h4>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {description}
            </p>
            {metric && (
              <div className={`mt-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                isDarkMode 
                  ? 'bg-blue-900/50 text-blue-300' 
                  : 'bg-blue-100 text-blue-800'
              }`}>
                {metric}
              </div>
            )}
          </div>
        </div>
        <div className="relative">
          <div className={`w-12 h-6 rounded-full transition-colors duration-300 ${
            checked 
              ? isDarkMode 
                ? 'bg-blue-500' 
                : 'bg-blue-500'
              : isDarkMode 
                ? 'bg-gray-700' 
                : 'bg-gray-200'
          }`}>
            <div className={`absolute w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-300 top-0.5 ${
              checked ? 'translate-x-6 left-1' : 'translate-x-0 left-1'
            }`} />
          </div>
        </div>
      </div>
    </button>
  </div>
);

const SummarySection = ({ isDarkMode, edgeServerEndpoint, timezone = 'Etc/UTC', globalSettings, handleGlobalChange }) => {
  const [loading, setLoading] = useState(true);
  const [expandedCard, setExpandedCard] = useState(null);
  const [stats, setStats] = useState({
    totalRecordings: 0,
    todayRecordings: 0,
    errors: 0,
    warnings: 0,
    userLogins: 0,
    totalUsers: 0
  });
  const [detailData, setDetailData] = useState({
    recordings: [],
    logs: [],
    users: {}
  });

  // edgeServerEndpoint already includes /api prefix, so don't add it again
  const API_BASE_URL = edgeServerEndpoint || '';

  useEffect(() => {
    fetchSummaryData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchSummaryData, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edgeServerEndpoint]);

  const fetchSummaryData = async () => {
    try {
      setLoading(true);
      
      // Fetch recordings
      let allRecordings = [];
      let todayRecordings = 0;
      try {
        // edgeServerEndpoint already includes /api, so use it directly
        const recordingsUrl = API_BASE_URL.startsWith('/api') 
          ? `${API_BASE_URL}/recordings`
          : `${API_BASE_URL}/api/recordings`;
        const recordingsResponse = await axios.get(recordingsUrl);
        // The API returns an array directly
        if (Array.isArray(recordingsResponse.data)) {
          allRecordings = recordingsResponse.data;
        } else if (recordingsResponse.data && typeof recordingsResponse.data === 'object') {
          // If it's an object, try to extract an array from it
          allRecordings = Object.values(recordingsResponse.data).flat();
        }
        
        // Get today's date string in the local timezone (YYYY-MM-DD format)
        const now = new Date();
        const todayDateStr = new Intl.DateTimeFormat('en-CA', { 
          timeZone: timezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        }).format(now);
        
        // Count recordings from today (recordings are stored in UTC)
        todayRecordings = allRecordings.filter(recording => {
          if (!recording.timestamp) return false;
          
          let recordingDateUTC;
          if (typeof recording.timestamp === 'string') {
            // Check if timestamp is in YYYYMMDD_HHMMSS format (from database)
            if (/^\d{8}_\d{6}$/.test(recording.timestamp)) {
              // Parse YYYYMMDD_HHMMSS format (stored as UTC in database)
              const datePart = recording.timestamp.substring(0, 8); // YYYYMMDD
              const timePart = recording.timestamp.substring(9, 15); // HHMMSS
              const year = parseInt(datePart.substring(0, 4));
              const month = parseInt(datePart.substring(4, 6)) - 1; // Month is 0-indexed
              const day = parseInt(datePart.substring(6, 8));
              const hour = parseInt(timePart.substring(0, 2));
              const minute = parseInt(timePart.substring(2, 4));
              const second = parseInt(timePart.substring(4, 6));
              
              // Create UTC date (database stores as UTC)
              recordingDateUTC = new Date(Date.UTC(year, month, day, hour, minute, second));
            } else {
              // Try parsing as ISO string or other format
              recordingDateUTC = new Date(recording.timestamp);
              if (isNaN(recordingDateUTC.getTime())) {
                return false;
              }
            }
          } else {
            recordingDateUTC = new Date(recording.timestamp);
          }
          
          if (isNaN(recordingDateUTC.getTime())) {
            return false;
          }
          
          // Convert UTC timestamp to local timezone date string (YYYY-MM-DD)
          const recordingDateStr = new Intl.DateTimeFormat('en-CA', {
            timeZone: timezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }).format(recordingDateUTC);
          
          // Compare date strings
          return recordingDateStr === todayDateStr;
        }).length;
      } catch (recordingsError) {
        console.error('Error fetching recordings:', recordingsError);
        console.error('Recordings response:', recordingsError.response?.data);
      }

      // Fetch logs for errors and warnings
      const todayStr = new Date().toISOString().split('T')[0];
      let errors = 0;
      let warnings = 0;
      let logsResponse = null;
      
      try {
        const logsUrl = API_BASE_URL.startsWith('/api') 
          ? `${API_BASE_URL}/logs?date=${todayStr}`
          : `${API_BASE_URL}/api/logs?date=${todayStr}`;
        logsResponse = await axios.get(logsUrl);
        if (Array.isArray(logsResponse.data)) {
          logsResponse.data.forEach(log => {
            const level = log.level?.toLowerCase() || '';
            if (level === 'error') errors++;
            if (level === 'warning') warnings++;
          });
        }
      } catch (logError) {
        console.error('Error fetching logs:', logError);
      }

      // Fetch users for login count
      let userLogins = 0;
      let totalUsers = 0;
      let usersData = {};
      try {
        const usersUrl = API_BASE_URL.startsWith('/api') 
          ? `${API_BASE_URL}/users`
          : `${API_BASE_URL}/api/users`;
        const usersResponse = await axios.get(usersUrl);
        usersData = usersResponse.data || {};
        
        // Ensure users is an object (not an array or string)
        if (typeof usersData === 'object' && !Array.isArray(usersData)) {
          // Count only top-level keys (email addresses)
          const userKeys = Object.keys(usersData).filter(key => {
            // Filter out any non-email keys (like metadata, etc.)
            const userData = usersData[key];
            return userData && typeof userData === 'object' && (userData.name || userData.role || userData.email);
          });
          totalUsers = userKeys.length;
          
          // Count logins from today (in local timezone)
          const now = new Date();
          const todayDateStr = new Intl.DateTimeFormat('en-CA', { 
            timeZone: timezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }).format(now);
          
          userKeys.forEach(email => {
            const user = usersData[email];
            if (user && user.login_history && Array.isArray(user.login_history)) {
              user.login_history.forEach(login => {
                if (login && login.timestamp) {
                  const loginDateUTC = new Date(login.timestamp);
                  if (!isNaN(loginDateUTC.getTime())) {
                    // Convert UTC to local timezone date string
                    const loginDateStr = new Intl.DateTimeFormat('en-CA', {
                      timeZone: timezone,
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit'
                    }).format(loginDateUTC);
                    if (loginDateStr === todayDateStr) {
                      userLogins++;
                    }
                  }
                }
              });
            }
          });
        }
      } catch (userError) {
        console.error('Error fetching users:', userError);
      }

      setStats({
        totalRecordings: allRecordings.length,
        todayRecordings: todayRecordings,
        errors,
        warnings,
        userLogins,
        totalUsers
      });
      
      // Store detailed data for expandable sections
      setDetailData({
        recordings: allRecordings,
        logs: logsResponse?.data || [],
        users: usersData
      });
    } catch (error) {
      console.error('Error fetching summary data:', error);
    } finally {
      setLoading(false);
    }
  };

  const containerClasses = isDarkMode
    ? 'bg-gray-900/60 border-gray-800'
    : 'bg-white border-gray-200';

  const cardClasses = isDarkMode
    ? 'bg-gray-800/50 border-gray-700'
    : 'bg-gray-50 border-gray-200';

  const statCards = [
    {
      icon: Database,
      label: 'Recordings',
      value: stats.totalRecordings,
      subValue: stats.todayRecordings,
      color: 'blue',
      description: `${stats.todayRecordings} today • ${stats.totalRecordings} total`
    },
    {
      icon: AlertTriangle,
      label: 'Errors',
      value: stats.errors,
      subValue: stats.warnings,
      color: 'red',
      description: `${stats.warnings} warnings • ${stats.errors} errors today`
    },
    {
      icon: Users,
      label: 'Users',
      value: stats.totalUsers,
      subValue: stats.userLogins,
      color: 'purple',
      description: `${stats.userLogins} logins today • ${stats.totalUsers} total users`
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: isDarkMode 
        ? 'bg-blue-900/30 border-blue-700 text-blue-300' 
        : 'bg-blue-50 border-blue-200 text-blue-700',
      green: isDarkMode 
        ? 'bg-green-900/30 border-green-700 text-green-300' 
        : 'bg-green-50 border-green-200 text-green-700',
      red: isDarkMode 
        ? 'bg-red-900/30 border-red-700 text-red-300' 
        : 'bg-red-50 border-red-200 text-red-700',
      yellow: isDarkMode 
        ? 'bg-yellow-900/30 border-yellow-700 text-yellow-300' 
        : 'bg-yellow-50 border-yellow-200 text-yellow-700',
      purple: isDarkMode 
        ? 'bg-purple-900/30 border-purple-700 text-purple-300' 
        : 'bg-purple-50 border-purple-200 text-purple-700',
      indigo: isDarkMode 
        ? 'bg-indigo-900/30 border-indigo-700 text-indigo-300' 
        : 'bg-indigo-50 border-indigo-200 text-indigo-700'
    };
    return colors[color] || colors.blue;
  };

  const getIconColorClasses = (color) => {
    const colors = {
      blue: isDarkMode ? 'bg-blue-600' : 'bg-blue-500',
      green: isDarkMode ? 'bg-green-600' : 'bg-green-500',
      red: isDarkMode ? 'bg-red-600' : 'bg-red-500',
      yellow: isDarkMode ? 'bg-yellow-600' : 'bg-yellow-500',
      purple: isDarkMode ? 'bg-purple-600' : 'bg-purple-500',
      indigo: isDarkMode ? 'bg-indigo-600' : 'bg-indigo-500'
    };
    return colors[color] || colors.blue;
  };

  const toggleCard = (cardKey) => {
    setExpandedCard(expandedCard === cardKey ? null : cardKey);
  };

  // Group recordings by day
  const groupRecordingsByDay = () => {
    const grouped = {};
    detailData.recordings.forEach(recording => {
      if (!recording.timestamp) return;
      
      let recordingDateUTC;
      if (typeof recording.timestamp === 'string') {
        if (/^\d{8}_\d{6}$/.test(recording.timestamp)) {
          const datePart = recording.timestamp.substring(0, 8);
          const timePart = recording.timestamp.substring(9, 15);
          const year = parseInt(datePart.substring(0, 4));
          const month = parseInt(datePart.substring(4, 6)) - 1;
          const day = parseInt(datePart.substring(6, 8));
          const hour = parseInt(timePart.substring(0, 2));
          const minute = parseInt(timePart.substring(2, 4));
          const second = parseInt(timePart.substring(4, 6));
          recordingDateUTC = new Date(Date.UTC(year, month, day, hour, minute, second));
        } else {
          recordingDateUTC = new Date(recording.timestamp);
        }
      } else {
        recordingDateUTC = new Date(recording.timestamp);
      }
      
      if (isNaN(recordingDateUTC.getTime())) return;
      
      const dateStr = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(recordingDateUTC);
      
      if (!grouped[dateStr]) {
        grouped[dateStr] = [];
      }
      grouped[dateStr].push(recording);
    });
    
    // Sort by date (newest first)
    return Object.keys(grouped).sort().reverse().map(date => ({
      date,
      recordings: grouped[date],
      count: grouped[date].length
    }));
  };

  // Format duration helper
  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  // Get errors and warnings from logs
  const getErrorsAndWarnings = () => {
    const errors = [];
    const warnings = [];
    
    if (Array.isArray(detailData.logs)) {
      detailData.logs.forEach(log => {
        const level = log.level?.toLowerCase() || '';
        if (level === 'error') {
          errors.push(log);
        } else if (level === 'warning') {
          warnings.push(log);
        }
      });
    }
    
    return { errors, warnings };
  };

  // Get users with login history
  const getUsersWithLogins = () => {
    const users = [];
    if (typeof detailData.users === 'object' && !Array.isArray(detailData.users)) {
      Object.keys(detailData.users).forEach(email => {
        const userData = detailData.users[email];
        if (userData && typeof userData === 'object' && (userData.name || userData.role || userData.email)) {
          users.push({
            email,
            name: userData.name || email,
            role: userData.role || 'User',
            login_history: userData.login_history || []
          });
        }
      });
    }
    return users.sort((a, b) => (b.login_history?.length || 0) - (a.login_history?.length || 0));
  };

  return (
    <div className={`rounded-2xl shadow-lg p-4 md:p-6 transition-colors duration-300 border ${containerClasses}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-2xl shadow-md ${isDarkMode ? 'bg-blue-600' : 'bg-blue-500'}`}>
            <TrendingUp className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Summary</h2>
            <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              High-level overview of system statistics and activity.
            </p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {statCards.map((stat, index) => {
              const Icon = stat.icon;
              const cardKey = stat.label.toLowerCase();
              const isExpanded = expandedCard === cardKey;
              return (
                <div key={index} className="col-span-full md:col-span-1">
                  <div
                    onClick={() => toggleCard(cardKey)}
                    className={`rounded-xl border-2 p-6 transition-all duration-300 hover:shadow-lg cursor-pointer ${getColorClasses(stat.color)}`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className={`p-3 rounded-lg ${getIconColorClasses(stat.color)}`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      {isExpanded ? (
                        <ChevronUp className={`w-5 h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
                      ) : (
                        <ChevronDown className={`w-5 h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
                      )}
                    </div>
                    <div className="mb-2">
                      {stat.subValue !== undefined ? (
                        <>
                          <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {stat.value.toLocaleString()}
                          </p>
                          <p className={`text-lg font-semibold mt-1 ${
                            stat.color === 'red' 
                              ? (isDarkMode ? 'text-yellow-300' : 'text-yellow-600')
                              : stat.color === 'purple'
                              ? (isDarkMode ? 'text-purple-300' : 'text-purple-600')
                              : (isDarkMode ? 'text-blue-300' : 'text-blue-600')
                          }`}>
                            {stat.subValue.toLocaleString()} {stat.color === 'red' ? 'warnings' : stat.color === 'purple' ? 'logins today' : 'today'}
                          </p>
                        </>
                      ) : (
                        <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {stat.value.toLocaleString()}
                        </p>
                      )}
                      <p className={`text-sm font-medium mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {stat.label}
                      </p>
                    </div>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {stat.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Detail Section - Full Width */}
          {expandedCard && (
            <div className={`mt-6 rounded-xl border-2 p-6 ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              {expandedCard === 'recordings' && <RecordingsDetail recordingsByDay={groupRecordingsByDay()} isDarkMode={isDarkMode} timezone={timezone} formatDuration={formatDuration} />}
              {expandedCard === 'errors' && <ErrorsWarningsDetail errorsAndWarnings={getErrorsAndWarnings()} isDarkMode={isDarkMode} timezone={timezone} />}
              {expandedCard === 'users' && <UsersDetail users={getUsersWithLogins()} isDarkMode={isDarkMode} timezone={timezone} />}
            </div>
          )}

          {/* Device Management Section */}
          {globalSettings && handleGlobalChange && (
            <div className="mt-8">
              <SettingsSectionHeader
                icon={SmartphoneNfc}
                title="Device Management"
                description="Enable automatic detection and connection to compatible radio devices"
                isDarkMode={isDarkMode}
                iconColor="purple"
              />
              <div className="mt-4 space-y-4">
                <Toggle
                  checked={String(globalSettings.enable_uniden_scanners).toLowerCase() === "true"}
                  onChange={(checked) => handleGlobalChange("enable_uniden_scanners", checked ? "True" : "False")}
                  label="Enable Uniden Scanners"
                  icon={SmartphoneNfc}
                  description="Automatically scan for and connect to Uniden BC125AT scanners when the application starts. This keeps your scanner inventory up to date."
                  isDarkMode={isDarkMode}
                />
                
                <Toggle
                  checked={String(globalSettings.enable_usb_audio_devices).toLowerCase() === "true"}
                  onChange={(checked) => handleGlobalChange("enable_usb_audio_devices", checked ? "True" : "False")}
                  label="Enable USB Recorders"
                  icon={Usb}
                  description="Automatically scan for and connect to USB audio devices when the application starts. This enables recording from USB audio interfaces."
                  isDarkMode={isDarkMode}
                />
                
                <Toggle
                  checked={String(globalSettings.enable_edge_devices).toLowerCase() === "true"}
                  onChange={(checked) => handleGlobalChange("enable_edge_devices", checked ? "True" : "False")}
                  label="Enable Boondock Edge Recorders"
                  icon={Cpu}
                  description="Automatically discover and connect to ESP32-based Boondock Edge recorders (Silicon Labs CP210x USB devices) when the service starts."
                  metric="Requires restart"
                  isDarkMode={isDarkMode}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Recordings Detail Component
const RecordingsDetail = ({ recordingsByDay, isDarkMode, timezone, formatDuration }) => {
  return (
    <div>
      <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
        Recordings by Day
      </h3>
      {recordingsByDay.length === 0 ? (
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>No recordings found</p>
      ) : (
        <div className="space-y-4">
          {recordingsByDay.map((day, idx) => (
            <div key={idx} className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-white'}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Calendar className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                  <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {new Date(day.date + 'T00:00:00').toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric',
                      timeZone: timezone
                    })}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1">
                    <FileAudio className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                    <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {day.count} recording{day.count !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </div>
              <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {day.recordings.slice(0, 5).map((rec, recIdx) => {
                  let timeStr = '';
                  if (rec.timestamp) {
                    if (/^\d{8}_\d{6}$/.test(rec.timestamp)) {
                      const timePart = rec.timestamp.substring(9, 15);
                      const hour = timePart.substring(0, 2);
                      const minute = timePart.substring(2, 4);
                      const second = timePart.substring(4, 6);
                      timeStr = `${hour}:${minute}:${second}`;
                    } else {
                      try {
                        const date = new Date(rec.timestamp);
                        timeStr = date.toLocaleTimeString('en-US', { timeZone: timezone, hour: '2-digit', minute: '2-digit', second: '2-digit' });
                      } catch (e) {
                        timeStr = rec.timestamp;
                      }
                    }
                  }
                  return (
                    <div key={recIdx} className="flex items-center gap-2 py-1">
                      <Clock className={`w-3 h-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                      <span>{timeStr}</span>
                      {rec.filename && (
                        <span className={`truncate max-w-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {rec.filename.split('/').pop()}
                        </span>
                      )}
                    </div>
                  );
                })}
                {day.count > 5 && (
                  <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    +{day.count - 5} more recording{day.count - 5 !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Errors and Warnings Detail Component
const ErrorsWarningsDetail = ({ errorsAndWarnings, isDarkMode, timezone }) => {
  const { errors, warnings } = errorsAndWarnings;
  
  return (
    <div>
      <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
        Errors and Warnings
      </h3>
      <div className="space-y-4">
        {errors.length > 0 && (
          <div>
            <h4 className={`text-md font-medium mb-2 flex items-center gap-2 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
              <AlertTriangle className="w-4 h-4" />
              Errors ({errors.length})
            </h4>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {errors.map((error, idx) => (
                <div key={idx} className={`p-3 rounded-lg ${isDarkMode ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
                  <div className={`text-sm font-medium ${isDarkMode ? 'text-red-300' : 'text-red-800'}`}>
                    {error.message || error.msg || 'Error'}
                  </div>
                  {error.timestamp && (
                    <div className={`text-xs mt-1 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                      {new Date(error.timestamp).toLocaleString('en-US', { timeZone: timezone })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {warnings.length > 0 && (
          <div>
            <h4 className={`text-md font-medium mb-2 flex items-center gap-2 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
              <AlertCircle className="w-4 h-4" />
              Warnings ({warnings.length})
            </h4>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {warnings.map((warning, idx) => (
                <div key={idx} className={`p-3 rounded-lg ${isDarkMode ? 'bg-yellow-900/20 border border-yellow-800' : 'bg-yellow-50 border border-yellow-200'}`}>
                  <div className={`text-sm font-medium ${isDarkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                    {warning.message || warning.msg || 'Warning'}
                  </div>
                  {warning.timestamp && (
                    <div className={`text-xs mt-1 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                      {new Date(warning.timestamp).toLocaleString('en-US', { timeZone: timezone })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {errors.length === 0 && warnings.length === 0 && (
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            No errors or warnings found for today
          </p>
        )}
      </div>
    </div>
  );
};

// Users Detail Component
const UsersDetail = ({ users, isDarkMode, timezone }) => {
  return (
    <div>
      <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
        Users and Login History
      </h3>
      {users.length === 0 ? (
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>No users found</p>
      ) : (
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {users.map((user, idx) => (
            <div key={idx} className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-white'}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {user.name}
                  </div>
                  <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {user.email}
                  </div>
                </div>
                <div className={`px-2 py-1 rounded text-xs font-medium ${
                  user.role === 'admin' 
                    ? (isDarkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700')
                    : (isDarkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700')
                }`}>
                  {user.role}
                </div>
              </div>
              {user.login_history && user.login_history.length > 0 ? (
                <div className="mt-3">
                  <div className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Recent Logins ({user.login_history.length})
                  </div>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {user.login_history.slice(0, 5).map((login, loginIdx) => {
                      let loginTime = '';
                      if (login.timestamp) {
                        try {
                          loginTime = new Date(login.timestamp).toLocaleString('en-US', { timeZone: timezone });
                        } catch (e) {
                          loginTime = login.timestamp;
                        }
                      }
                      return (
                        <div key={loginIdx} className={`text-xs flex items-center gap-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          <Activity className="w-3 h-3" />
                          <span>{loginTime}</span>
                        </div>
                      );
                    })}
                    {user.login_history.length > 5 && (
                      <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        +{user.login_history.length - 5} more login{user.login_history.length - 5 !== 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className={`text-xs mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  No login history
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SummarySection;

